<template>
    <section>
        <div v-if="!open && status === 'uncompleted'"
             class="buyflow-panel bg-gray-200">
            <div class="font-bold text-lg text-gray-600">
                Self Install Eligibility
            </div>
        </div>
        <div v-else
            class="buyflow-panel">
            <div class="space-y-6"
                :class="open? 'rounded-t-md': 'rounded-md'">

                <CheckoutCardBar
                    :open="open"
                    :status="status"
                    :heading="headingCopy"
                    :description="description"
                    :show-description="!open && status === 'completed'"
                    @edit="edit"
                    @cancel-edit="cancelEdit"
                />
                <SelfInstallQuestions
                    v-if="open"
                    :questions-and-answers="questions"
                    @answer="updateEligibility($event, questions)"
                />

                <button v-if="open"
                        type="button"
                        class="btn-primary mr-5 flex items-center text-sm widescreen:text-base"
                        :disabled="allAnswered === false"
                        @click="setInstall(showIneligible? professional: selfInstall, showIneligible? 'pro': 'sik')">
                    <IconCircleNotch class="animate-spin text-white mr-2 text-lg" v-if="isLoading" />
                    {{ (forceSelfInstall && !isFiber) || isOhio? 'Submit' : (showIneligible ? 'Continue with Pro Install' : 'Continue with Self Install')}}
                </button>
            </div>
            <div v-if="isEligible && !open"
                 class="bg-white -mx-6 px-6 pt-6 mt-4 rounded-b-md border-t-2">
                <div class="pb-4">
                    <h2 class="text-base desktop:text-lg mb-0.5">
                        Good news! We'll be sending you a self installation kit so you can get online faster!
                    </h2>
                    <div v-if="isOhio" class="mb-3">
                        <div class="text-sm desktop:text-base">
                            The benefits of Self Install:
                            <ul class="mx-6 my-3">
                                <li class="list-disc">
                                    Contact-free way to get set up and connected fast
                                </li>
                                <li class="list-disc">
                                    <a class="underline" href="https://www.youtube.com/watch?v=hYgheNSb-pU" target="_blank">Self-Installation Kit is easy to use</a> and is delivered directly to your door<br>(no one is required to be home when your kit is delivered)*
                                </li>
                                <li class="list-disc">
                                    A Certified Tech will call you in advance of the delivery
                                </li>
                            </ul>
                        </div>
                         <small v-if="internetEquipmentSelection({ name: 'Customer Owned Modem' })">
                             <p class="text-sm desktop:text-base">*A Breezeline technician will leave a Customer Owned Modem self install kit at your door on your install date. Once you have installed your own modem, please contact the call center at <strong>888-536-9600</strong> to activate your service.</p>
                         </small>
                    </div>
                    <div v-else class="mb-3">
                        <div class="text-sm desktop:text-base">
                            The benefits of Self Install:
                            <ul class="mx-6 my-3">
                                <li class="list-disc">
                                    Get connected <strong>fast</strong> and <strong>contact-free</strong> with our Self-Installation Kit
                                </li>
                                <li class="list-disc">
                                    Our easy-to-use <a class="underline" href="https://www.youtube.com/watch?v=hYgheNSb-pU" target="_blank">Self-Installation Kit</a> is delivered directly to your door.  No one is required to be home when your kit is delivered (within 3-5 business days).
                                </li>
                                <li class="list-disc">
                                    Set it up anytime that is convenient for you.
                                </li>
                                <li class="list-disc">
                                    We're here to help.  Give us a call with any questions.
                                </li>
                            </ul>
                        </div>
                         <small v-if="internetEquipmentSelection({ name: 'Customer Owned Modem' })">
                             <p class="text-sm desktop:text-base">*Since you've selected to use your own modem, a Breezeline technician will leave a <strong>Customer Owned Modem self install kit</strong> at your door on your install date. Once you have installed your own modem, please contact the call center at <strong>888-536-9600</strong> to activate your service.</p>
                         </small>
                    </div>
                </div>
            </div>
            <div v-else-if="!isEligible && !open && ((forceSelfInstall && !isFiber) || isOhio)"
                 :class="showIneligible? 'bg-white -mx-6 px-6 pt-6 mt-4 rounded-b-md border-t-2 ': ''">
                <div v-if="showIneligible" class="pb-4">
                    <h2 class="text-base desktop:text-lg mb-0.5">
                        Your service will be professionally installed
                    </h2>
                    <div class="mb-3">
                        <div class="text-sm desktop:text-base">
                            The benefits of Professional Install:
                            <ul class="mx-6 my-3">
                                <li class="list-disc">
                                    A Certified Tech will arrive at your home to install and set up your service
                                </li>
                                <li class="list-disc">
                                    Outdoor check of signal strength to validate your home's connection
                                </li>
                                <li class="list-disc">
                                    You can choose your date and time for install at checkout
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div v-if="showIneligible && open"
                     class="flex">
                    <button
                        type="button"
                        class="btn-primary mr-5 text-sm widescreen:text-base"
                        @click="setInstall(professional, 'pro')"
                        data-cy="button-schedule-time"
                    >
                        Submit
                    </button>
                </div>
            </div>
            <div v-else-if="!isEligible && !open && !forceSelfInstall"
                 class="bg-white -mx-6 px-6 pt-6 mt-4 rounded-b-md border-t-2">
                <h2 v-if="showIneligible"
                    class="text-base desktop:text-lg mb-3">
                    Your service does not qualify for self-installation
                </h2>
                <div
                     class="flex">
                    <button
                        type="button"
                        class="btn-primary mr-5 text-sm widescreen:text-base"
                        @click="setInstall(professional, 'pro')"
                        data-cy="button-schedule-time"
                    >
                        Check Schedule/Delivery Times
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script>

import {mapGetters, mapState} from "vuex";
import PricePerMonth from "../../../../components/atoms/PricePerMonth"
import IconCheck from "@/atoms/icons/IconCheck"
import IconCircleNotch from "@/atoms/icons/IconCircleNotch"
import SelfInstallQuestions from "./questions"
import CheckoutCardBar from "../CheckoutCardBar/index.vue";
import {DateTime as dt} from "luxon";

export default {
    name: "CheckoutSelfInstall",
    components: {
        CheckoutCardBar,
        PricePerMonth,
        IconCheck,
        IconCircleNotch,
        SelfInstallQuestions,
    },
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        status: {
            type: String,
            default: 'uncompleted',
        },
        hasSelectedSelfInstall: {
            type: Boolean,
            required: true,
        },
        installIsScheduled: {
            type: Boolean,
            required: false
        },
        isTechDelivered: {
            type: Boolean,
            required: false,
        },
        installData: {
            type: Object,
            required: true,
        },
        internetEquipmentSelection: {
            type: Function,
            required: true,
        },
        setInstallType: {
            type: Function,
            default: () => {},
        },
        fetchSchedulerTimes: {
            type: Function,
            default: () => {},
        },
    },
    data: () => ({
        isLoading: false,
        breezelineLastProvider: null,
        currentConnection: null,
        setupLocation: null,
        installOption: 'pro',
        description: 'Self Install selected',
        isEligible: false,
        allAnswered: false,
        selfInstallQuizCompleted: false,
        showIneligible: false,
        questions: [
            {
                name: 'cableOutlet',
                question: 'Is there a cable outlet inside your home?',
                modal: {
                    trigger: "(What’s a cable outlet?)",
                    content: "<span class='flex flex-row gap-2 items-center'>Locate a cable wall outlet (coaxial) in a central location. It will look like this: <img class='max-w-1/3' alt='cable wall outlet' src='/assets/images/buyflow/cable-wall-outlet.png'></span>"
                },
                gaEvent: ['resi: checkout', 'resi: checkout confirmation - self installation question - is there a coax cable inside', 'resi: checkout confirmation page'],
                answers: [
                    {
                        title: 'Yes',
                        value: true
                    },
                    {
                        title: 'No',
                        value: false
                    },
                    {
                        title: 'Not Sure',
                        value: 'not sure',
                    },
                ]
            },
            {
                name: 'keepingServices',
                question: 'Will you be keeping any services from your current provider?',
                gaEvent: ['resi: checkout', 'resi: checkout confirmation - self installation question - will you be keeping any current services', 'resi: checkout confirmation page'],
                answers:[
                    {
                        title: 'Yes',
                        value: false
                    },
                    {
                        title: 'No',
                        value: true
                    },
                ]
            },
            {
                name: 'specificCurrentProvider',
                question: 'Do you currently have AT&T U-verse as an internet provider?',
                gaEvent: ['resi: checkout', 'resi: checkout confirmation - self installation question - do you currently have at&t u-verse', 'resi: checkout confirmation page'],
                answers:[
                    {
                        title: 'Yes',
                        value: false
                    },
                    {
                        title: 'No',
                        value: true
                    }
                ]
            }
        ]
    }),
    computed: {
        ...mapGetters('Buyflow',[
            'isOhio',
        ]),
        ...mapGetters('Buyflow/tv',[
            'getServiceType',
        ]),
        ...mapState([
            'meta'
        ]),
        professional() {
            return this.installData?.proInstall ? this.installData.proInstall : ''
        },
        selfInstall() {
            return this.installData?.selfInstallKit ? this.installData.selfInstallKit : ''
        },
        headingCopy() {
            return this.isOhio ? 'Installation Details' : 'Let’s check your self install eligibility'
        },
        forceSelfInstall() {
            return true;
        },
        isFiber() {
            return ['fiber-equal-speed','fiber','fiber-competitive','fiber-competitive-equal-speed'].includes(this.meta.marketType)
        },
    },
    watch: {},
    created () {},
    mounted () {
        this.installOption = this.hasSelectedSelfInstall ? 'sik': 'pro'
    },
    updated () {},
    destroyed () {},
    methods: {
        updateEligibility(answers, questions){
            const eligible = answers.every(a => a === true || a === 'not sure')

            if (answers.length === questions.length || !eligible) { // all questions are answered or one answer has determined ineligibility
                this.isEligible = eligible
                this.showIneligible = !eligible
            } else {
                this.showIneligible = false
            }

            if(answers.length === questions.length) {
                this.allAnswered = true
            }
        },
        edit() {
            this.allAnswered = false
            this.$emit('set-finalize-state', {
                open: 'selfInstall',
            })
            this.$emit('edit-self-Install')
        },

        cancelEdit(){
            this.$emit('set-finalize-state', {
                open: 'scheduler',
            })
        },
        setInstall(install, type){
            if (this.isLoading) return // prevent double click
            this.isLoading = true

            switch (type){
                case 'pro':
                    this.description = this.isEligible?
                        'Eligible for Self Install, Professional Install selected':
                        'Not Eligible for Self Install, Professional Install selected'
                    this.description = this.isOhio ? 'Professional Install selected' : this.description
                    this.sendEvent('resi: checkout', 'resi: checkout confirmation - declined self installation', 'resi: checkout confirmation page')
                    break
                case 'sik':
                    this.description = 'Self Install selected'
                    this.sendEvent('resi: checkout', 'resi: checkout confirmation - self installation', 'resi: checkout confirmation page')
                    break
            }
            this.$emit('clear-schedule')
            this.setInstallType(install)
                .finally(() => {
                    setTimeout(() => {
                        if (install?.name === 'SIK - Ship Direct') {
                            this.fetchSchedulerTimes({ startingDate: dt.now().toFormat('yyyy-LL-dd') + 'T00:00:00.000Z', autoSelect: true})
                                .then(() => {
                                    this.$emit('set-finalize-state', {
                                        open: 'confirmation',
                                        selfInstall: 'completed',
                                        scheduler: 'completed',
                                    })
                                    this.isLoading = false
                                })
                        } else {
                            this.$emit('set-finalize-state', {
                                open: 'scheduler',
                                selfInstall: 'completed',
                                scheduler: 'uncompleted',
                            })
                            this.isLoading = false
                        }
                    }, 2000)
                })
        }
    },
}
</script>

<style scoped>

</style>
